import { RequestedOnMarketplaceEnum } from '@/@types/Enums/marketplace'
import Loader from '@/components/Loader/Loader'
import CommissionInput from '@/components/shared/CommissionInput/CommissionInput'
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect'
import Divider from '@/components/shared/Divider/Divider'
import { Input } from '@/components/shared/Input/Input'
import { Label } from '@/components/shared/Label/Label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { AffiliateProgramResponse } from '@/services/affiliate-program.service'
import useModalStore from '@/store/useModalStore'
import { isMarketplaceEnabled } from '@/utils/isMarketplaceEnabled'
import {
  isValidHttpUrl,
  normalizeUrlOnBlur,
  normalizeUrlWhenPaste,
} from '@/utils/normalize'
import { AlertCircle, HelpCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useConfigureAffiliateProgram } from '../hooks/useConfigureAffiliateProgram'
import { currencyOptions, formatCurrency } from '@/lib/select-options'
import { usePermissions } from '@/auth/PermissionsContext'

interface ConfigureAffiliateForm {
  cookieDuration: string
  contact_email: string
  description: string
  supportPhone: string
  page_url: string
  attribution: string
  enable_affiliate_program: boolean
  manual_affiliation_approval: boolean
  access_buyer_contact_info: boolean
  requested_on_marketplace: RequestedOnMarketplaceEnum
  pre_cbk_responsible: string
  commission_rule_type: string
  // commission_value: valor da comissão (interpretação depende do tipo)
  // - percentage: ex. 21
  // - cpa: valor em centavos
  commission_value: number
  commission?: number
  currency?: string
}

export default function ConfigureAffiliateSheet({
  affiliateProgram,
  isLoading,
  productId,
}: {
  affiliateProgram: AffiliateProgramResponse | null
  isLoading: boolean
  productId: string
}) {
  const { t } = useTranslation(['affiliates', 'common'])
  const { closeModal } = useModalStore()
  const [formattedPrice, setFormattedPrice] = useState('')
  const { hasPermission } = usePermissions()
  const canRequestMarketplace = hasPermission('affiliates.request_marketplace')
  const canSetPreCbkResponsible = hasPermission('affiliates.set_pre_chargeback_responsible')

  const {
    configureAffiliateProgram,
    isConfiguring,
  } = useConfigureAffiliateProgram()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    clearErrors,
    formState: { errors, isSubmitting, defaultValues },
  } = useForm<ConfigureAffiliateForm>({
    defaultValues: {
      cookieDuration: '30',
      contact_email: '',
      description: '',
      supportPhone: '',
      page_url: '',
      attribution: 'last_click',
      enable_affiliate_program: false,
      manual_affiliation_approval: false,
      access_buyer_contact_info: false,
      requested_on_marketplace: RequestedOnMarketplaceEnum.DISABLED,
      pre_cbk_responsible: 'proportional',
      commission_rule_type: 'percentage',
      commission_value: 1,
      currency: 'BRL',
      commission: null,
    },
  })

  const enableAffiliate = watch('enable_affiliate_program')
  const approveIndividually = watch('manual_affiliation_approval')
  const allowBuyerAccess = watch('access_buyer_contact_info')
  const commissionRuleType = watch('commission_rule_type')
  const currency = watch('currency')
  const commissionValue = watch('commission_value')

  useEffect(() => {
    if (affiliateProgram && affiliateProgram.data) {
      setValue(
        'commission_rule_type',
        affiliateProgram.data.commission_rule_type || 'percentage',
      )
      setValue('commission_value', affiliateProgram.data.commission_value ?? 1)
      setValue('cookieDuration', affiliateProgram.data.cookie_duration || '30')
      setValue('contact_email', affiliateProgram.data.contact_email || '')
      setValue('attribution', affiliateProgram.data.attribution || 'last_click')
      setValue('page_url', affiliateProgram.data.page_url || '')
      setValue('description', affiliateProgram.data.description || '')
      setValue(
        'enable_affiliate_program',
        affiliateProgram.data.enable_affiliate_program,
      )
      setValue(
        'manual_affiliation_approval',
        affiliateProgram.data.manual_affiliation_approval,
      )
      setValue(
        'access_buyer_contact_info',
        affiliateProgram.data.access_buyer_contact_info,
      )
      setValue(
        'requested_on_marketplace',
        affiliateProgram.data.requested_on_marketplace,
      )
      const preCbkResponsible = affiliateProgram.data.pre_cbk_responsible
      setValue(
        'pre_cbk_responsible',
        preCbkResponsible && preCbkResponsible.trim() !== ''
          ? preCbkResponsible
          : 'proportional',
      )
      setValue('currency', affiliateProgram.data.currency || 'BRL')
    }
  }, [affiliateProgram, setValue])

  const onSubmit = async (data: ConfigureAffiliateForm) => {
    const payload = {
      productId,
      contact_email: data.contact_email,
      attribution: data.attribution,
      description: data.description,
      cookie_duration: parseInt(data.cookieDuration, 10),
      commission_rule_type: data.commission_rule_type,
      currency: data.currency,
      commission_value: Number(data.commission_value),
      commission: 1,
      enable_affiliate_program: data.enable_affiliate_program,
      manual_affiliation_approval: data.manual_affiliation_approval,
      access_buyer_contact_info: data.access_buyer_contact_info,
      requested_on_marketplace: data.requested_on_marketplace,
      pre_cbk_responsible: data.pre_cbk_responsible || 'proportional',
      page_url: data.page_url,
    }

    await configureAffiliateProgram(payload)
  }

  const responsibleOptions = [
    { value: 'producer', label: t('producer') },
    { value: 'proportional', label: t('proportional') },
    { value: 'affiliate', label: t('affiliate') },
  ]

  const requestedMarketplace = watch('requested_on_marketplace')
  const isMarketplaceApproved =
    requestedMarketplace === RequestedOnMarketplaceEnum.APPROVED
  const isMarketplacePending =
    requestedMarketplace === RequestedOnMarketplaceEnum.PENDING
  const isMarketplaceRejected =
    requestedMarketplace === RequestedOnMarketplaceEnum.REJECTED

  const commissionTypeOptions = [
    { value: 'percentage', label: t('percentage') },
    { value: 'cpa', label: t('fixed') },
  ]

  const resetCommissionByType = (nextType: string) => {
    clearErrors('commission_value')

    if (nextType === 'cpa') {
      const c = currency || defaultValues?.currency || 'BRL'
      const cents = 100 // R$ 1,00 (ou equivalente) em centavos
      setValue('commission_value', cents, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setFormattedPrice(formatCurrency(cents / 100, c))
      return
    }

    // percentage
    setValue('commission_value', 1, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
    setFormattedPrice('')
  }

  const handlePriceChange = (e, setFormattedValue, fieldName) => {
    const rawValue = e.target.value.replace(/\D/g, '')
    const valueInCents = parseInt(rawValue || '0', 10)
    setFormattedValue(
      formatCurrency(valueInCents / 100, currency || defaultValues?.currency),
    )
    setValue(fieldName, valueInCents, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  useEffect(() => {
    if (commissionRuleType !== 'cpa') return

    const c = currency || defaultValues?.currency
    if (!c) return

    setFormattedPrice(formatCurrency((commissionValue || 0) / 100, c))
  }, [commissionRuleType, commissionValue, currency, defaultValues?.currency])
  const isMarketplaceToggleDisabled =
    !isMarketplaceEnabled || isMarketplacePending

  return (
    <section className="sm:overflow-y-hidden h-full flex flex-col justify-between">
      <SheetHeader className="p-6">
        <SheetTitle className="flex gap-2">
          {t('configure_affiliate_program')}
        </SheetTitle>
      </SheetHeader>
      {isLoading ? (
        <div className="h-full flex items-center justify-center">
          <Loader />
        </div>
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 justify-between overflow-y-auto"
        >
          <section className="flex flex-col gap-4 px-6">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={enableAffiliate}
                  defaultChecked={defaultValues.enable_affiliate_program}
                  onCheckedChange={(checked) =>
                    setValue('enable_affiliate_program', checked)
                  }
                  data-cy="switch-enable-affiliate-program"
                />
                <span className="text-sm">{t('enable_affiliate_program')}</span>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={approveIndividually}
                  defaultChecked={defaultValues.manual_affiliation_approval}
                  onCheckedChange={(checked) =>
                    setValue('manual_affiliation_approval', checked)
                  }
                  data-cy="switch-manual-approval"
                />
                <span className="text-sm">{t('approve_individually')}</span>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={allowBuyerAccess}
                  defaultChecked={defaultValues.access_buyer_contact_info}
                  onCheckedChange={(checked) =>
                    setValue('access_buyer_contact_info', checked)
                  }
                  data-cy="switch-allow-buyer-access"
                />
                <span className="text-sm">{t('allow_buyer_access')}</span>
              </div>

              {canRequestMarketplace && (
                <div className="flex items-center space-x-2">
                  <Switch
                    disabled={isMarketplaceToggleDisabled}
                    checked={isMarketplaceApproved || isMarketplacePending}
                    defaultChecked={isMarketplaceApproved}
                    onCheckedChange={(checked) => {
                      if (isMarketplaceApproved && !checked) {
                        setValue(
                          'requested_on_marketplace',
                          RequestedOnMarketplaceEnum.DISABLED,
                        )
                      } else if (!isMarketplaceApproved && checked) {
                        setValue(
                          'requested_on_marketplace',
                          RequestedOnMarketplaceEnum.PENDING,
                        )
                        setValue('enable_affiliate_program', true)
                      }
                    }}
                    data-cy="switch-enable-marketplace"
                  />
                  <span className="text-sm inline-flex items-center gap-1.5">
                    {t('common:enable_marketplace')}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex text-muted-foreground hover:text-colorText"
                            aria-label={t('common:internalMarketplaceTooltip')}
                          >
                            <HelpCircle className="h-4 w-4 cursor-help" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[300px] px-4 py-3 !overflow-visible">
                          <p className="text-xs text-muted leading-relaxed break-words whitespace-normal">
                            {t('common:internalMarketplaceTooltip')}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </span>
                </div>
              )}
              {canRequestMarketplace && !isMarketplaceEnabled && (
                <div className="mt-2 relative overflow-hidden rounded-lg border border-primary/30 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-3">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-primary/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
                  <div className="flex items-center gap-3 relative">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-primary text-lg">✨</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-primary uppercase tracking-wide">
                        {t('common:coming_soon')}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {t('common:marketing')}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {canRequestMarketplace && isMarketplacePending && (
                <Alert variant="warning" className="mt-2 border-warning/50">
                  <AlertCircle className="h-5 w-5 text-warning flex-shrink-0" />
                  <div>
                    <AlertTitle className="text-warning font-semibold text-sm mb-1">
                      {t('affiliates:requested_on_marketplace_pending_title')}
                    </AlertTitle>
                    <AlertDescription className="text-sm text-muted-foreground leading-relaxed mt-1">
                      {t('affiliates:requested_on_marketplace_pending_message')}
                    </AlertDescription>
                  </div>
                </Alert>
              )}
              {canRequestMarketplace && isMarketplaceRejected && (
                <Alert variant="destructive" className="mt-2">
                  <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
                  <div>
                    <AlertTitle className="text-destructive font-semibold text-sm mb-1">
                      {t('affiliates:requested_on_marketplace_rejected_title')}
                    </AlertTitle>
                    <AlertDescription className="text-sm text-muted-foreground leading-relaxed mt-1">
                      {t(
                        'affiliates:requested_on_marketplace_rejected_message',
                      )}
                    </AlertDescription>
                  </div>
                </Alert>
              )}
            </div>

            <Divider />

            <div>
              <p className="text-sm font-semibold leading-[24px] mb-4">
                {t('details')}
              </p>
              <div className="mb-4">
                <Label htmlFor="page_url" className="mb-2">
                  {t('common:detailsAffiliatePage')}{' '}
                  <span className="text-gray-400">({t('optional')})</span>
                </Label>
                <Input
                  {...register('page_url', {
                    setValueAs: (v) => normalizeUrlOnBlur(String(v ?? '')),
                    validate: (value) =>
                      !value ||
                      value === 'https://' ||
                      isValidHttpUrl(value) ||
                      t('common:validationInvalidSalesPage'),
                  })}
                  placeholder="https://"
                  className="mt-2"
                  onBlur={(e) => {
                    if (!e.target.value) {
                      e.target.value = 'https://'
                      setValue('page_url', 'https://')
                    }
                  }}
                  onPaste={(e) => {
                    const finalValue = normalizeUrlWhenPaste(e)
                    setValue('page_url', finalValue)
                  }}
                  helperText={errors?.page_url?.message}
                  data-cy="input-affiliate-page-url"
                />
              </div>
              <div className="mb-4 flex items-center gap-2">
                <Controller
                  control={control}
                  name="commission_rule_type"
                  render={({ field }) => (
                    <CustomSelect
                      label={t('commission_type')}
                      value={field.value || 'percentage'}
                      options={commissionTypeOptions}
                      placeholder={t('select_commission_type')}
                      onValueChange={(value) => {
                        field.onChange(value)
                        setValue('commission_rule_type', value, {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        })
                        resetCommissionByType(value)
                      }}
                    />
                  )}
                />
              </div>
              {commissionRuleType === 'percentage' && (
                <CommissionInput
                  label={t('commission')}
                  helperText={errors.commission_value?.message}
                  minValue={1}
                  maxValue={99}
                  defaultValue={affiliateProgram?.data?.commission_value ?? 1}
                  onChangeCommission={(value) =>
                    setValue(
                      'commission_value',
                      Number.parseFloat(value || '0'),
                      {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      },
                    )
                  }
                  dataCy="input-affiliate-commission"
                  {...register('commission_value', {
                    required: t('commission_required'),
                    validate: {
                      min: (value) => value >= 1 || t('commission_min'),
                      max: (value) => value <= 99 || t('commission_max'),
                    },
                  })}
                />
              )}
              {commissionRuleType === 'cpa' && (
                <div className="flex items-center gap-2">
                  <Controller
                    control={control}
                    name="currency"
                    render={({ field }) => (
                      <CustomSelect
                        value={field.value || 'BRL'}
                        label={t('common:currency')}
                        placeholder={t('select_commission_type')}
                        onValueChange={(value) => {
                          field.onChange(value)
                          setValue('currency', value, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                          })
                        }}
                        options={currencyOptions}
                        className="w-[100px]"
                        id="currencySelect"
                      />
                    )}
                  />
                  <Input
                    value={formattedPrice}
                    onChange={(e) => {
                      handlePriceChange(
                        e,
                        setFormattedPrice,
                        'commission_value',
                      )
                      clearErrors('commission_value')
                    }}
                    label={t('commission')}
                    placeholder={t('value_placeholder')}
                    data-cy="input-offer-price"
                  />
                </div>
              )}
              {errors.commission_value && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.commission_value.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="description" className="">
                {t('description')}
              </Label>
              <Textarea
                {...register('description')}
                placeholder={t('description')}
                maxLength={500}
                data-cy="input-affiliate-description"
                className={`focus:outline-none mt-2 ring-0 focus-visible:outline-none ${
                  errors?.description ? 'border-error' : 'border-colorBorder'
                }`}
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs text-zinc-500">
                  {watch('description')?.length}/500
                </span>
              </div>

              {canSetPreCbkResponsible && (
                <Controller
                  name="pre_cbk_responsible"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t('common:required_field'),
                    },
                  }}
                  render={({ field }) => (
                    <CustomSelect
                      placeholder={t('common:selectAnOption')}
                      label={t('cbk_responsible')}
                      options={responsibleOptions}
                      value={field.value || ''}
                      helperText={errors.pre_cbk_responsible?.message}
                      dataCy="select-pre-cbk-responsible"
                      onValueChange={(value) => {
                        field.onChange(value)
                      }}
                    />
                  )}
                />
              )}
            </div>

            <div>
              <Label htmlFor="radio-group">{t('choose_option')}</Label>
              <RadioGroup
                value={watch('attribution')}
                onValueChange={(value) => setValue('attribution', value)}
                data-cy="radio-attribution"
                className="flex items-center justify-between max-md:flex-col max-md:items-start mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="last_click"
                    id="last_click"
                    data-cy="radio-last-click"
                  />
                  <Label htmlFor="last_click">{t('last_click')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="first_click"
                    id="first_click"
                    data-cy="radio-first-click"
                  />
                  <Label htmlFor="first_click">{t('first_click')}</Label>
                </div>
              </RadioGroup>
              {errors.attribution && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.attribution.message}
                </p>
              )}
            </div>

            <div className="w-[220px]">
              <p className="text-sm mb-2">{t('cookie_duration')}</p>
              <Select
                value={watch('cookieDuration')}
                onValueChange={(value) => setValue('cookieDuration', value)}
              >
                <SelectTrigger
                  defaultValue={String(defaultValues?.cookieDuration || '30')}
                  className="w-full h-9 text-sm"
                  data-cy="select-cookie-duration"
                >
                  <SelectValue placeholder={t('cookie_duration')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30" data-cy="option-cookie-30">
                    {t('daysOptions.30')}
                  </SelectItem>
                  <SelectItem value="60" data-cy="option-cookie-60">
                    {t('daysOptions.60')}
                  </SelectItem>
                  <SelectItem value="90" data-cy="option-cookie-90">
                    {t('daysOptions.90')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Divider />

            <div>
              <p className="text-sm font-semibold leading-[24px] mb-4">
                {t('affiliate_support')}{' '}
                <span className="text-gray-400">({t('optional')})</span>
              </p>
              <Input
                label={t('common:email')}
                placeholder={t('support_email_placeholder')}
                {...register('contact_email')}
                className="mb-3"
                type="email"
                data-cy="input-support-email"
              />
            </div>
          </section>

          <div className="flex items-center justify-end p-4 border-t max-sm:pb-12">
            <Button
              type="button"
              variant="ghost"
              onClick={() => closeModal('config-affiliate')}
              data-cy="btn-cancel-affiliate-config"
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isConfiguring}
              data-cy="btn-save-affiliate-config"
            >
              {isConfiguring ? t('saving') : t('save_changes')}
            </Button>
          </div>
        </form>
      )}
    </section>
  )
}
